import { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlus, Star, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { productService, type GalleryImage } from '../services/productService';
import { fieldClass, labelClass } from '../utils/form';

type Props = {
  productId: string;
  readOnly?: boolean;
};

export function ProductImageGallery({ productId, readOnly = false }: Props) {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productService.listImages(productId);
      setImages(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت گالری');
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onFiles = async (files: FileList | null) => {
    if (!files?.length || readOnly) return;
    setUploading(true);
    try {
      let first = images.length === 0;
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name}: فقط تصویر مجاز است`);
          continue;
        }
        await productService.uploadImage(productId, file, {
          alt: file.name,
          isMain: first,
        });
        first = false;
      }
      toast.success('تصاویر آپلود شدند');
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'آپلود ناموفق بود');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const setMain = async (imageId: string) => {
    try {
      const next = await productService.patchImage(productId, imageId, { isMain: true });
      setImages(next);
      toast.success('تصویر اصلی تنظیم شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    }
  };

  const remove = async (imageId: string) => {
    if (!confirm('این تصویر حذف شود؟')) return;
    try {
      await productService.deleteImage(productId, imageId);
      toast.success('حذف شد');
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const updateAlt = async (imageId: string, alt: string) => {
    try {
      const next = await productService.patchImage(productId, imageId, { alt });
      setImages(next);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره توضیح ناموفق بود');
    }
  };

  return (
    <div className="space-y-3 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">گالری تصاویر</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            چند تصویر اضافه کنید؛ تام‌نیل خودکار (~۳۰۰px) برای لیست ساخته می‌شود
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            افزودن عکس
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => void onFiles(e.target.files)}
        />
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-6 text-center">در حال بارگذاری گالری...</p>
      ) : images.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-lg">
          هنوز تصویری ثبت نشده است
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((img) => {
            const src = img.thumbnailUrl || img.url;
            return (
              <div key={img.id} className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                <div className="relative aspect-square bg-slate-100">
                  {src ? (
                    <img src={src} alt={img.alt || ''} className="w-full h-full object-cover" />
                  ) : null}
                  {img.isMain && (
                    <span className="absolute top-2 right-2 text-[10px] font-bold bg-amber-400 text-amber-950 px-2 py-0.5 rounded-full">
                      اصلی
                    </span>
                  )}
                </div>
                <div className="p-2 space-y-2">
                  {!readOnly && (
                    <div>
                      <label className={labelClass}>توضیح (alt)</label>
                      <input
                        className={fieldClass}
                        defaultValue={img.alt}
                        onBlur={(e) => {
                          if (e.target.value !== img.alt) void updateAlt(img.id, e.target.value);
                        }}
                      />
                    </div>
                  )}
                  {!readOnly && (
                    <div className="flex items-center gap-1">
                      {!img.isMain && (
                        <button
                          type="button"
                          title="تصویر اصلی"
                          onClick={() => void setMain(img.id)}
                          className="flex-1 inline-flex items-center justify-center gap-1 text-xs py-1.5 rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100"
                        >
                          <Star className="h-3.5 w-3.5" />
                          اصلی
                        </button>
                      )}
                      <button
                        type="button"
                        title="حذف"
                        onClick={() => void remove(img.id)}
                        className="flex-1 inline-flex items-center justify-center gap-1 text-xs py-1.5 rounded-md text-red-700 bg-red-50 hover:bg-red-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        حذف
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
